import { useState, useEffect } from 'react';
import { useSettings } from '@/contexts/SettingsContext';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

interface DeliveryMethodSelectProps {
  onSelectMethod: (method: 'delivery' | 'pickup') => void;
  className?: string;
  enableDelivery?: boolean;
  enablePickup?: boolean;
}

export function DeliveryMethodSelect({ 
  onSelectMethod, 
  className,
  enableDelivery: propEnableDelivery,
  enablePickup: propEnablePickup
}: DeliveryMethodSelectProps) {
  const { checkoutOptions } = useSettings();
  const [selectedMethod, setSelectedMethod] = useState<'delivery' | 'pickup'>('pickup');
  
  const enableDelivery = propEnableDelivery ?? checkoutOptions.productDelivery?.enableDelivery ?? true;
  const enablePickup = propEnablePickup ?? checkoutOptions.productDelivery?.enablePickup ?? true;
  
  // Set default method based on available options
  useEffect(() => {
    if (enableDelivery) {
      setSelectedMethod('delivery');
    } else if (enablePickup) {
      setSelectedMethod('pickup');
    }
  }, [enableDelivery, enablePickup]);
  
  // Notify parent component when method changes
  useEffect(() => {
    onSelectMethod(selectedMethod);
  }, [selectedMethod, onSelectMethod]);
  
  // Don't render if neither option is enabled
  if (!enableDelivery && !enablePickup) {
    return null;
  }
  
  // If only one option is enabled, select it and don't show the tabs
  if (!enableDelivery || !enablePickup) {
    return null;
  }
  
  return (
    <div className={cn("space-y-3", className)}>
      <h3 className="text-base font-medium">Delivery Method</h3>
      
      <Tabs 
        value={selectedMethod} 
        onValueChange={(value) => setSelectedMethod(value as 'delivery' | 'pickup')}
        className="w-full"
      >
        <TabsList className="grid grid-cols-2 w-full bg-white">
          {enableDelivery && (
            <TabsTrigger value="delivery" className="bg-white">Delivery</TabsTrigger>
          )}
          {enablePickup && (
            <TabsTrigger value="pickup" className="bg-white">Pickup</TabsTrigger>
          )}
        </TabsList>
      </Tabs>
      
      <div className="p-3 bg-muted/30 rounded-md text-sm">
        {selectedMethod === 'delivery' ? (
          <p>We'll deliver your order to your location. Select a delivery location below.</p>
        ) : (
          <p>You'll pick up your order from our store location.</p>
        )}
      </div>
    </div>
  );
} 